import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { haptic } from "@/lib/haptic"

/**
 * Squared-off (10px), app-wide. One press treatment.
 *
 * This used to carry FOUR ways of saying "pressed": the elevate pseudo-element
 * overlays (`hover-elevate active-elevate-2`, an `::after` at z-index 999 on
 * every button), a global `button:active` keyframe in index.css, its own
 * `active:scale-[0.98]`, and the haptic. The overlay is also where the
 * pseudo-element collision class of bug lived — see the `hit-target` note on
 * `size: icon`. Now: the fill lifts its shadow on hover, the box scales to .97
 * on press, and on a touch device the global `:active` opacity dip in
 * index.css does the same job without a hover state that sticks. The haptic
 * stays; it is not visual.
 *
 * SHAPE: buttons are squared-off and badges are pills — the reverse of PropPay,
 * on Andrew's call (2026-09-09), and the rule is only that the two differ: a
 * control and a status must never share a silhouette.
 *
 * Focus ring is 2px with a 2px offset. WCAG 2.2 SC 2.4.11 (Focus Appearance)
 * requires the indicator to be at least as large as a 2px-thick perimeter and
 * to hit 3:1 against adjacent colours; the offset renders it in the parent's
 * background colour so the ring reads on cards, toolbars and the sidebar alike.
 */
const buttonVariants = cva(
  [
    // A pill, like PropPay LX's (Andrew, 2026-11-02: "pill shaped CTAs and
    // tabs… another great way to separate ourselves from competitors"). It
    // reverses 2026-09-27's squared buttons for what is pressed: buttons,
    // tabs and menu rows. Status chips and badges keep their 4px corner.
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold text-body",
    "transition-[transform,background-color,border-color,box-shadow,color,opacity] duration-instant ease-standard",
    "active:scale-[.97]",
    "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    // Communicates the disabled state to assistive tech, which `disabled:opacity-50`
    // alone does not — opacity is invisible to a screen reader.
    "disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        /* `default` and `primary` are the same thing. `default` is the name
           every existing call site relies on (it is the cva default, so most
           name nothing); `primary` is what the design system calls it. */
        default: "bg-primary text-primary-foreground hover:shadow-md",
        primary: "bg-primary text-primary-foreground hover:shadow-md",
        // `destructive`, not `coral`: coral is also the buyer-journey colour
        // for the Firm stage, and a Delete button must not share it.
        /* PropPay's delete button (2026-09-27): the failed tint with its own
           ink and a hairline of the same hue, not a solid red slab. The
           confirmation dialogs keep a solid fill on their final action. */
        destructive: "border border-on-tint-destructive/25 bg-tint-destructive text-on-tint-destructive hover:border-on-tint-destructive/40 hover:bg-tint-destructive/75",
        /* The outline is `border-strong`, the 3:1 control outline — not
           `--button-outline`, an 8% black that measured well under 1.4.11 on
           a cream card. */
        /* PropPay's neutral button: a whisper of fill inside the control
           outline, so it reads as a button on a card and not as a hole in it. */
        outline: "border border-border-strong bg-foreground/[.04] text-foreground hover:bg-foreground/[.07]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        // A transparent border so that toggling a border on later doesn't shift layout.
        ghost: "border border-transparent text-foreground hover:bg-foreground/[.06]",
      },
      // Heights are MIN heights, so a button that is handed a lot of content
      // grows rather than clipping.
      //
      // The `touch:` variants raise each size to Apple's 44pt minimum on coarse
      // pointers ONLY. Doing it unconditionally would inflate every dense
      // desktop toolbar in the app; doing it not at all leaves a 36–40px target
      // under a ~44px fingertip, which is the single most common reason a web
      // app feels imprecise on a phone.
      size: {
        default: "min-h-10 touch:min-h-touch px-5 py-2",
        sm: "min-h-9 touch:min-h-touch touch:min-w-touch px-4 text-label",
        lg: "min-h-11 touch:min-h-touch px-6 py-2.5",
        // On a touch screen an icon button is a REAL 44px box (Andrew,
        // 2026-11-03: "the whole app should be" spaced for touch). It used to
        // keep its 36px (32px) box and grow only an invisible hit area
        // (.hit-target), and two of them side by side — Move Up, Move Down,
        // Remove on each purchaser, 4px apart — had overlapping hit areas, so
        // a thumb aimed at Move Down could land on Remove. A caller's own
        // `size-8` sets the desk size only; `touch:size-11` still wins under
        // a finger. `hit-target` stays for the desk box, which it leaves
        // alone on a mouse (index.css).
        icon: "h-9 w-9 touch:size-11 shrink-0 hit-target",
        iconSm: "h-8 w-8 touch:size-11 shrink-0 hit-target [&_svg]:size-3.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        haptic();
        onClick?.(e);
      },
      [onClick],
    )
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={handleClick}
        // Safari Tabs past a <button> unless it sets tabindex (see ui/select). A caller's own tabIndex wins.
        tabIndex={0}
        // Read by ActionsRow: a header shows its primary and folds the rest into ⋯.
        data-variant={variant ?? "default"}
        {...props}
      />
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
